import { useState ,useEffect, useMemo} from 'react';
import { ContractorList } from '../../../components/ContractorList';
import { ContractorDetails } from '../../../components/ContractorDetails';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Contractor } from '../../../types';
import { ArrowLeft, CalendarDays, ChevronDown, Download, Filter, Search, ShieldCheck, Users, X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CallAPIGetPromise } from 'Common/commaon';
import { ContractorListSkeleton } from '../../../components/shimmer';
import { downloadCsv } from '../../../helpers/csvExport';
import ContractorDocumentStatusList from './ContractorDocumentStatusList';
import './all-contractors.css';

const PROFESSION_FIELDS = [
  'ua_profile',
  'profile',
  'profession',
  'trade',
  'cat_name',
  'catName',
  'ua_skills',
  'skills',
];

const collectProfessionLabels = (value: any): string[] => {
  if (!value) return [];

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed || trimmed === '[]' || trimmed === '{}') return [];

    try {
      return collectProfessionLabels(JSON.parse(trimmed));
    } catch {
      return trimmed
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectProfessionLabels(item));
  }

  if (typeof value === 'object') {
    const label =
      value.label ||
      value.name ||
      value.cat_name ||
      value.catName ||
      value.trade ||
      value.profession ||
      value.value;

    return label ? [String(label).trim()] : [];
  }

  return [];
};

const getContractorProfessions = (contractor: any): string[] => {
  const professions = PROFESSION_FIELDS.flatMap((field) =>
    collectProfessionLabels(contractor?.[field])
  ).filter((label) => label && !/^\d+$/.test(label));

  return Array.from(new Set(professions));
};

const getContractorState = (contractor: any): string => {
  const directState =
    contractor?.state ||
    contractor?.ua_state ||
    contractor?.company_state ||
    contractor?.address_state ||
    contractor?.user_address?.state ||
    contractor?.company_address?.state ||
    contractor?.address?.state;

  if (directState) return String(directState).trim();

  const addressValues = [
    contractor?.ua_address,
    contractor?.user_address,
    contractor?.company_address,
  ];
  for (const addressValue of addressValues) {
    if (typeof addressValue !== 'string') continue;

    try {
      const parsedAddress = JSON.parse(addressValue);
      if (parsedAddress?.state) return String(parsedAddress.state).trim();
    } catch {
      // Some records contain plain-text addresses, so they cannot provide a reliable state filter.
    }
  }

  return '';
};

const getDateTime = (value: any) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const toFilterDateValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDate = (value: any) => {
  if (!value) return '';

  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }).format(date);
};

export default function NewContractorDetails() {
  const [selectedContractor, setSelectedContractor] = useState<
    Contractor | string | number | null
  >(null);
  const [activeView, setActiveView] = useState<'directory' | 'documents'>('directory');
  const [refresh, setRefresh] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [professionFilter, setProfessionFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [quickFilter, setQuickFilter] = useState<'all' | 'latest' | 'subscribed' | 'not-subscribed'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortType, setSortType] = useState('maxProjects');
  const [loading,setLoading] =useState(false)
 const [contractors,setContractor] =useState<any[]>([])
  const professionOptions = Array.from(
    new Set(contractors.flatMap((contractor) => getContractorProfessions(contractor)))
  ).sort((a, b) => a.localeCompare(b));
  const stateOptions = Array.from(
    new Set(contractors.map((contractor) => getContractorState(contractor)).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const latestContractorIds = useMemo(
    () =>
      new Set(
        [...contractors]
          .sort((a, b) => (getDateTime(b.created_at) || 0) - (getDateTime(a.created_at) || 0))
          .slice(0, 5)
          .map((contractor) => contractor.id)
      ),
    [contractors]
  );

  const filteredContractors = contractors.filter(contractor => {
    const query = searchQuery.trim().toLowerCase();
    const professions = getContractorProfessions(contractor);
    const contractorState = getContractorState(contractor);
    const contractorPlan = String(contractor.active_plan || 'none').toLowerCase();
    const isSubscribed = contractorPlan !== 'none';
    const contractorStatus = contractor.is_active === 1 ? 'active' : 'paused';
    const totalProjects = Number(contractor.total_projects) || 0;
    const createdAt = getDateTime(contractor.created_at);
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
    const matchesSearch =
      !query ||
      contractor.user_first_name?.toLowerCase().includes(query) ||
      contractor.company_name?.toLowerCase().includes(query) ||
      contractor.user_email?.toLowerCase().includes(query) ||
      contractor.user_last_name?.toLowerCase().includes(query) ||
      professions.some((profession) => profession.toLowerCase().includes(query));
    const matchesProfession =
      professionFilter === 'all' ||
      professions.some(
        (profession) => profession.toLowerCase() === professionFilter.toLowerCase()
      );
    const matchesState =
      stateFilter === 'all' || contractorState.toLowerCase() === stateFilter.toLowerCase();
    const matchesStatus = statusFilter === 'all' || contractorStatus === statusFilter;
    const matchesPlan = planFilter === 'all' || contractorPlan === planFilter;
    const matchesQuickFilter =
      quickFilter === 'all' ||
      quickFilter === 'latest' ||
      (quickFilter === 'subscribed' && isSubscribed) ||
      (quickFilter === 'not-subscribed' && !isSubscribed);
    const matchesProjects =
      projectFilter === 'all' ||
      (projectFilter === 'none' && totalProjects === 0) ||
      (projectFilter === '1-5' && totalProjects >= 1 && totalProjects <= 5) ||
      (projectFilter === '6-10' && totalProjects >= 6 && totalProjects <= 10) ||
      (projectFilter === '11+' && totalProjects >= 11);
    const matchesDateFrom = fromTime === null || (createdAt !== null && createdAt >= fromTime);
    const matchesDateTo = toTime === null || (createdAt !== null && createdAt <= toTime);

    return (
      matchesSearch &&
      matchesProfession &&
      matchesState &&
      matchesStatus &&
      matchesPlan &&
      matchesQuickFilter &&
      matchesProjects &&
      matchesDateFrom &&
      matchesDateTo
    );
  }).sort((a, b) => {
    if (quickFilter === 'latest' || sortType === 'latest') {
      return (getDateTime(b.created_at) || 0) - (getDateTime(a.created_at) || 0);
    }
    if (sortType === 'oldest') {
      return (getDateTime(a.created_at) || 0) - (getDateTime(b.created_at) || 0);
    }
    if (sortType === 'name') {
      const firstName = `${a.user_first_name || ''} ${a.user_last_name || ''}`.trim();
      const secondName = `${b.user_first_name || ''} ${b.user_last_name || ''}`.trim();
      return firstName.localeCompare(secondName);
    }
    return (Number(b.total_projects) || 0) - (Number(a.total_projects) || 0);
  });

  const activeFilterCount = [
    searchQuery,
    professionFilter !== 'all',
    stateFilter !== 'all',
    statusFilter !== 'all',
    planFilter !== 'all',
    projectFilter !== 'all',
    quickFilter !== 'all',
    dateFrom,
    dateTo,
  ].filter(Boolean).length;

  const handleClearFilters = () => {
    setSearchQuery('');
    setProfessionFilter('all');
    setStateFilter('all');
    setStatusFilter('all');
    setPlanFilter('all');
    setProjectFilter('all');
    setQuickFilter('all');
    setDateFrom('');
    setDateTo('');
    setSortType('maxProjects');
  };

  const handleExportCsv = () => {
    downloadCsv('contractors-report.csv', filteredContractors, [
      { header: 'S.No', value: (_contractor, index) => index + 1 },
      { header: 'ID', value: (contractor: any) => contractor.id },
      { header: 'First Name', value: (contractor: any) => contractor.user_first_name },
      { header: 'Last Name', value: (contractor: any) => contractor.user_last_name },
      {
        header: 'Name',
        value: (contractor: any) =>
          [contractor.user_first_name, contractor.user_last_name].filter(Boolean).join(' '),
      },
      { header: 'Email', value: (contractor: any) => contractor.user_email },
      { header: 'Phone', value: (contractor: any) => contractor.user_phone },
      { header: 'Company', value: (contractor: any) => contractor.company_name },
      { header: 'State', value: (contractor: any) => getContractorState(contractor) },
      {
        header: 'Professions',
        value: (contractor: any) => getContractorProfessions(contractor).join(', '),
      },
      { header: 'Plan', value: (contractor: any) => contractor.active_plan || 'Not Subscribed' },
      {
        header: 'Status',
        value: (contractor: any) => (contractor.is_active === 1 ? 'Active' : 'Paused'),
      },
      { header: 'Total Projects', value: (contractor: any) => contractor.total_projects },
      { header: 'Created Date', value: (contractor: any) => formatDate(contractor.created_at) },
    ]);
  };

  const handleSelectContractor = (contractor: any) => {
    setSelectedContractor(contractor.id);
  };

  const handleClearSelection = () => {
    setSelectedContractor(null);
  };

  useEffect(()=>{
    setLoading(true)
CallAPIGetPromise("/api/crm/contractors").then((res:any)=>{
    if(res.data.data[0]){
 setContractor(res.data.data)
    }
}).finally(()=>{
  setLoading(false)
})
  },[refresh])
  return (
    <div className="page-content tailwind-prefix all-contractors-page">
      {/* Header */}
      <div className="bg-white border rounded-lg mb-3">
        <div className="w-full px-4 py-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 shrink-0 bg-blue-600 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-gray-900">
                  {selectedContractor
                    ? 'Contractor Details'
                    : activeView === 'directory' ? 'Contractor List' : 'Contractor Compliance'}
                </div>
                <div className="text-gray-600">
                  {selectedContractor
                    ? 'Review profile, billing activity, projects and leads'
                    : activeView === 'directory'
                    ? 'Manage your contractor relationships'
                    : 'Track missing and verified profile documents'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 md:justify-end">
              {selectedContractor ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClearSelection}
                  className="bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to contractors
                </Button>
              ) : activeView === 'directory' && (
                <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportCsv}
                  disabled={filteredContractors.length === 0}
                  className="bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Total Contractors:</span>
                  <span className="text-gray-900">{contractors.length}</span>
                </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {!selectedContractor && (
        <div className="admin-contractor-tabs" role="tablist" aria-label="Contractor admin views">
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'directory'}
            className={activeView === 'directory' ? 'is-active' : ''}
            onClick={() => setActiveView('directory')}
          >
            <Users size={16} /> Contractor directory
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'documents'}
            className={activeView === 'documents' ? 'is-active' : ''}
            onClick={() => setActiveView('documents')}
          >
            <ShieldCheck size={16} /> Document status
          </button>
        </div>
      )}

      {/* Main Content */}
       <div className="w-full  py-3">
        <div className="grid grid-cols-1  gap-6">
          {/* Left Sidebar - Contractor List */}

          {!selectedContractor && activeView === 'directory' && <div>
            <div className="bg-white rounded-lg border p-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-gray-900">
                  <Filter className="h-4 w-4 text-blue-600" />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                      {activeFilterCount} active
                    </span>
                  )}
                </div>
                {activeFilterCount > 0 && (
                  <Button variant="outline" size="sm" onClick={handleClearFilters} className="bg-white">
                    <X className="h-4 w-4 mr-2" />
                    Clear all
                  </Button>
                )}
              </div>

              <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="relative">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                    <Search className="h-4 w-4 text-gray-500" />
                  </span>
                  <Input
                    type="text"
                    placeholder="Search contractors..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-gray-100 border-0"
                  />
                </div>
                <div className="relative">
                  <select
                    value={professionFilter}
                    onChange={(e) => setProfessionFilter(e.target.value)}
                    style={{ appearance: 'none' }}
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="all">All professions</option>
                    {professionOptions.map((profession) => (
                      <option key={profession} value={profession}>
                        {profession}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
                <div className="relative">
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    style={{ appearance: 'none' }}
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="all">All states</option>
                    {stateOptions.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ appearance: 'none' }}
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
                <div className="relative">
                  <select
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    style={{ appearance: 'none' }}
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="all">All subscriptions</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="none">Not subscribed</option>
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
                <div className="relative">
                  <select
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    style={{ appearance: 'none' }}
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="all">Any project count</option>
                    <option value="none">No projects</option>
                    <option value="1-5">1–5 projects</option>
                    <option value="6-10">6–10 projects</option>
                    <option value="11+">11+ projects</option>
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
                <div className="relative">
                  <DatePicker
                    selected={dateFrom ? new Date(`${dateFrom}T00:00:00`) : null}
                    onChange={(date: Date | null) =>
                      setDateFrom(date ? toFilterDateValue(date) : '')
                    }
                    selectsStart
                    startDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : null}
                    endDate={dateTo ? new Date(`${dateTo}T00:00:00`) : null}
                    maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="From: MM/DD/YYYY"
                    aria-label="Joined from"
                    wrapperClassName="w-full"
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <CalendarDays className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
                <div className="relative">
                  <DatePicker
                    selected={dateTo ? new Date(`${dateTo}T00:00:00`) : null}
                    onChange={(date: Date | null) =>
                      setDateTo(date ? toFilterDateValue(date) : '')
                    }
                    selectsEnd
                    startDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : null}
                    endDate={dateTo ? new Date(`${dateTo}T00:00:00`) : null}
                    minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="To: MM/DD/YYYY"
                    aria-label="Joined to"
                    wrapperClassName="w-full"
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <CalendarDays className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
                <div className="relative">
                  <select
                    value={sortType}
                    onChange={(e) => setSortType(e.target.value)}
                    style={{ appearance: 'none' }}
                    className="h-10 w-full rounded-md border border-gray-200 bg-gray-100 py-0 pl-3 pr-10 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="maxProjects">Sort: Most projects</option>
                    <option value="latest">Sort: Newest joined</option>
                    <option value="oldest">Sort: Oldest joined</option>
                    <option value="name">Sort: Name A–Z</option>
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  </span>
                </div>
              </div>

              <div className="mb-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                <span className="mr-1 text-sm text-gray-600">Quick filters:</span>
                {[
                  { value: 'all', label: 'All contractors', active: 'bg-gray-900 text-white border-gray-900' },
                  { value: 'latest', label: 'Latest', active: 'bg-blue-600 text-white border-blue-600' },
                  { value: 'subscribed', label: 'Subscribed', active: 'bg-emerald-600 text-white border-emerald-600' },
                  { value: 'not-subscribed', label: 'Not Subscribed', active: 'bg-rose-600 text-white border-rose-600' },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() => setQuickFilter(filter.value as typeof quickFilter)}
                    aria-pressed={quickFilter === filter.value}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      quickFilter === filter.value
                        ? filter.active
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              
              <div className="mb-3 flex items-center justify-between">
                <div className="text-gray-900">Contractors</div>
                <span className="text-gray-600">{filteredContractors.length} found</span>
              </div>
              {loading?<ContractorListSkeleton /> :             <div className="  ">
                <ContractorList
                  contractors={filteredContractors}
                  selectedContractor={selectedContractor}
                  onSelectContractor={handleSelectContractor}
                  latestContractorIds={latestContractorIds}
                />
              </div>}
              
  
            </div>
          </div>}

          {!selectedContractor && activeView === 'documents' && (
            <ContractorDocumentStatusList
              contractors={contractors}
              loading={loading}
              onViewContractor={handleSelectContractor}
              onRefresh={() => setRefresh((value) => !value)}
            />
          )}

          {/* Right Content - Contractor Details */}
          <div className={` ${!selectedContractor ? 'hidden ' : ''}`}>
            {selectedContractor ? (
              <div className="contractor-details-shell">
                <ContractorDetails
                  contractorId={selectedContractor}
                 goToBack={()=>{
                  handleClearSelection()
                  setRefresh(r=>!r)
                 }}
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg border p-12 text-center">
                <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-gray-900 mb-2">No Contractor Selected</h3>
                <div className="text-gray-600">
                  Select a contractor from the list to view their details, projects, leads, and more.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
    </div>
  );
}
